import axios from 'axios';
import { getApiWfAuthToken } from './oauthService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Runtime print service URL — set from config_settings on app load.
// Empty string means print requests go through the api-gateway (local dev).
let _printBaseUrl = '';

/** Set the print service URL (called from Layout.tsx on app load / config update). */
export const setPrintServiceUrl = (url: string) => {
  _printBaseUrl = (url || '').trim();
};

/** Get the current print service URL. Empty = use api-gateway proxy. */
export const getPrintServiceUrl = (): string => _printBaseUrl;

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // WCO-2914: attach the wf-auth token so HTE-gated routes reached via this instance
  // (e.g. /api/supervisor/dev-tools) pass the api-gateway re-check. Without this, dev-tools
  // 403s with HTE_ACCESS_NO_TOKEN once the gate is enabled — even for granted admins.
  const wfAuthToken = getApiWfAuthToken();
  if (wfAuthToken) {
    config.headers['x-wayfong-token'] = `Bearer ${wfAuthToken}`;
  }
  return config;
});

export interface ProductionSession {
  sub_session_number?: string; // For multi-station sub-sessions
  is_sub_session?: boolean; // Flag indicating this is a sub-session
  session_number: string;
  product_sku: string;
  product_name: string;
  product_chinese?: string;
  product_ingredient?: string; // From product_label_configuration.ingredient JOIN
  product_unit_weight?: number; // From inventory.unit_weight JOIN
  product_brand?: string; // From ics.inventory.english_brand_name - used for label production location
  box_target_weight: number;
  packing: number;
  yield_factor: number;
  station_id?: number; // Station ID from session
  station_number: number;
  station_name: string;
  station_type?: string;
  raw_material_item_number: string;
  raw_material_name: string;
  raw_material_chinese: string;
  raw_material_lot: string; // From production_session.raw_material_lot
  supervisor_name: string;
  session_status: string;
  status: string; // Database returns 'status' instead of 'session_status'
  initial_material_weight: number;
  target_output_weight: number;
  actual_output_weight: number;
  waste_weight: number;
  remainder_intact?: number; // Returns weight (remainder_intact from waste_tracking)
  yield_percentage: number;
  expected_yield: number;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  // Sub-unit tracking (for multi-pack products)
  item_sub_quantity?: number; // Number of sub-units per box (e.g., 4 for 4x10lb)
  sub_unit_weight?: number; // Weight of each sub-unit (e.g., 10 for 10lb bags)
  item_sub_unit?: string; // Sub-unit type (PC, BG, LB, etc.) - discrete units trigger sub-unit workflow
  paused_duration_seconds?: number; // Total seconds the session was paused
  weight_mode?: 'fixed' | 'catch'; // Weight entry mode: fixed (auto unit weight) or catch (manual entry)
  expected_boxes?: number; // Expected number of boxes to pack (for progress tracking)
  total_boxes?: number; // Current count of packed boxes
}

export interface OperatorStats {
  sessions_worked: number;
  total_output: number;
  total_waste: number;
  total_initial_material: number;
  avg_yield: number;
  products_produced: number;
}

export interface ProductionEntry {
  id: string;
  sku: string;
  item_number: string;
  session_number: string;
  lot_number: string;
  box_number: string;
  initial_balance: number;
  unit_weight: number;
  created_at: string;
}

export interface WasteEntry {
  id: string;
  waste_type: string;
  waste_weight: number;
  waste_reason: string;
  disposal_method?: string;
  recorded_at: string;
}

// Normalize yield fields from whole % (DB) to decimal (frontend).
// E.g. 92.2 → 0.922. Applied at the service boundary so all consumers get decimal.
const normalizeSessionYield = (session: any): any => {
  if (!session) return session;
  const s = { ...session };
  const fields = ['yield_percentage', 'expected_yield', 'raw_material_yield'];
  for (const f of fields) {
    if (f in s && s[f] != null) s[f] = Number(s[f]) / 100;
  }
  return s;
};

const operatorService = {
  // Get operator's current assigned session
  getMySession: async (operatorId: string): Promise<{ session: ProductionSession | null }> => {
    const response = await api.get(`/api/operator/sessions/my-session/${operatorId}`);
    const data = response.data;
    if (data.session) data.session = normalizeSessionYield(data.session);
    return data;
  },

  // Get available sessions pool - pass operator_id to get can_assign flag
  getAvailableSessions: async (operatorId?: string) => {
    const response = await api.get('/api/operator/sessions/available-pool', {
      params: operatorId ? { operator_id: operatorId } : undefined
    });
    const data = response.data;
    if (Array.isArray(data.sessions)) {
      data.sessions = data.sessions.map(normalizeSessionYield);
    }
    return data;
  },

  // Assign self to a session from the pool
  // For sub-sessions, pass is_sub_session: true and sessionId should be the sub_session_number
  assignSelfToSession: async (sessionId: number | string, operatorId: string, isSubSession: boolean = false) => {
    const response = await api.post(`/api/operator/sessions/${sessionId}/assign-self`, {
      operator_id: operatorId,
      is_sub_session: isSubSession
    });
    return response.data;
  },

  // Get operator statistics
  getStats: async (operatorId: string, period: 'today' | 'week' | 'month' = 'today'): Promise<OperatorStats> => {
    const response = await api.get(`/api/operator/sessions/stats/${operatorId}`, {
      params: { period }
    });
    const data = response.data;
    if (data.avg_yield != null) data.avg_yield = Number(data.avg_yield) / 100;
    return data;
  },

  // Get operator's session history
  getHistory: async (operatorId: string, limit: number = 20, offset: number = 0) => {
    const response = await api.get(`/api/operator/sessions/history/${operatorId}`, {
      params: { limit, offset }
    });
    const data = response.data;
    if (Array.isArray(data.sessions)) {
      data.sessions = data.sessions.map((s: any) => {
        if (s.yield_percentage != null) s.yield_percentage = Number(s.yield_percentage) / 100;
        return s;
      });
    }
    return data;
  },

  // Start a session or sub-session
  startSession: async (sessionId: number | string, operatorId: string, isSubSession: boolean = false): Promise<{ session: ProductionSession }> => {
    const response = await api.post(`/api/operator/sessions/${sessionId}/start`, {
      operator_id: operatorId,
      is_sub_session: isSubSession
    });
    const data = response.data;
    if (data.session) data.session = normalizeSessionYield(data.session);
    return data;
  },

  // Pause a session or sub-session
  pauseSession: async (
    sessionId: number | string,
    reason: string,
    isSubSession: boolean = false,
    employeeNumber?: string,
    role?: 'operator' | 'packer'
  ): Promise<{ session: ProductionSession; pause_id: string | null }> => {
    const response = await api.post(`/api/operator/sessions/${sessionId}/pause`, {
      reason,
      is_sub_session: isSubSession,
      employee_number: employeeNumber,
      role
    });
    const data = response.data;
    if (data.session) data.session = normalizeSessionYield(data.session);
    return data;
  },

  // Resume a session or sub-session
  resumeSession: async (
    sessionId: number | string,
    pauseId?: string,
    isSubSession: boolean = false,
    employeeNumber?: string,
    role?: 'operator' | 'packer'
  ): Promise<{ session: ProductionSession }> => {
    const response = await api.post(`/api/operator/sessions/${sessionId}/resume`, {
      pause_id: pauseId,
      is_sub_session: isSubSession,
      employee_number: employeeNumber,
      role
    });
    const data = response.data;
    if (data.session) data.session = normalizeSessionYield(data.session);
    return data;
  },

  // Cancel assignment - revert sub-session from 'ready' back to 'received'
  cancelAssignment: async (sessionId: number | string, operatorId: string, isSubSession: boolean = true): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/api/operator/sessions/${sessionId}/cancel-assignment`, {
      operator_id: operatorId,
      is_sub_session: isSubSession
    });
    return response.data;
  },

  // Complete a session or sub-session
  // completion_type: 'normal' (target reached) or 'override' (early completion by supervisor)
  // Note: yieldPercentage param is now decimal (e.g. 0.922). Converted to whole % for backend.
  completeSession: async (sessionId: number | string, notes?: string, isSubSession: boolean = false, yieldPercentage?: number, completionType?: 'normal' | 'override'): Promise<{ message: string; session: ProductionSession }> => {
    const response = await api.post(`/api/operator/sessions/${sessionId}/complete`, {
      notes,
      is_sub_session: isSubSession,
      yield_percentage: yieldPercentage != null ? yieldPercentage * 100 : undefined,
      completion_type: completionType
    });
    const data = response.data;
    if (data.session) data.session = normalizeSessionYield(data.session);
    return data;
  },

  // Record production (pack a box)
  recordProduction: async (data: {
    session_number: number | string;
    sub_session_number?: string; // For multi-station sub-sessions
    product_sku: string;
    actual_weight: number;
    operator_id: string | null;
    station_id: number;
    barcode_number?: string | null;
    sub_unit_ids?: number[]; // Array of sub-unit IDs to link to this master box
    status?: 'packed' | 'pending'; // Initial status (default: 'packed', use 'pending' for mobile flow requiring confirmation)
  }) => {
    const response = await api.post('/api/operator/production', data);
    return response.data;
  },

  // Get production entries for a session (optionally filtered by subsession)
  getProductionEntries: async (sessionId: number | string, limit: number = 50, offset: number = 0, subSessionNumber?: string) => {
    const response = await api.get(`/api/operator/production/session/${sessionId}`, {
      params: { limit, offset, sub_session_number: subSessionNumber }
    });
    return response.data;
  },

  // Delete a production entry (void box)
  deleteProductionEntry: async (inventoryId: string, reason: string) => {
    const response = await api.delete(`/api/operator/production/${inventoryId}`, {
      data: { reason }
    });
    return response.data;
  },

  // Void a box by barcode number (used when operator cancels label confirmation)
  voidBoxByBarcode: async (barcodeNumber: string, reason: string, operatorId?: string) => {
    const response = await api.delete(`/api/operator/production/barcode/${encodeURIComponent(barcodeNumber)}`, {
      data: { reason, operator_id: operatorId }
    });
    return response.data;
  },

  // Delete a pending box by barcode (used when print fails - label was never printed)
  // This completely removes the record instead of marking it as cancelled
  deletePendingBox: async (barcodeNumber: string) => {
    const response = await api.delete(`/api/operator/production/barcode/${encodeURIComponent(barcodeNumber)}/pending`);
    return response.data;
  },

  // ============================================
  // SUB-UNIT OPERATIONS
  // ============================================

  // Create a sub-unit (when operator weighs a bag before master box)
  // Backend auto-generates sub_unit_number based on existing orphaned sub-units
  // CHANGE LOG: Added session_number fallback (2026-01-20)
  createSubUnit: async (data: {
    sub_session_number: string;
    session_number?: string; // Fallback if sub_session_number is empty
    actual_weight: number;
  }) => {
    const response = await api.post('/api/operator/production/sub-units', data);
    return response.data;
  },

  // Get orphaned sub-units for a session (not yet linked to a master box)
  // Used to restore state on page refresh
  getOrphanedSubUnits: async (subSessionNumber: string) => {
    const response = await api.get(`/api/operator/production/sub-units/orphaned/${subSessionNumber}`);
    return response.data;
  },

  // Get sub-units for a master box
  getSubUnits: async (boxId: string) => {
    const response = await api.get(`/api/operator/production/${boxId}/sub-units`);
    return response.data;
  },

  // Delete a sub-unit (void)
  deleteSubUnit: async (subUnitId: number) => {
    const response = await api.delete(`/api/operator/production/sub-units/${subUnitId}`);
    return response.data;
  },

  // Record waste
  recordWaste: async (data: {
    sub_session_number: string; // Required - waste is tracked at sub-session level
    waste_type: string;
    waste_weight: number;
    waste_reason: string;
    disposal_method?: string;
    operator_id: string;
    quantity?: number; // Number of cases (for remainder_intact)
    // WCO-2545: per-case capture for intact remainders — one entry per physical leftover case the
    // supervisor scanned (barcode = full raw string) or manually added for a damaged label
    // (barcode ''/omitted → backend tallies it under the pooled MANUAL bucket). Drives the
    // per-barcode transfer-out cap. Optional & backward compatible (absent → legacy count-only).
    cases?: Array<{ barcode?: string; weight?: number }>;
  }) => {
    const response = await api.post('/api/operator/waste', data);
    return response.data;
  },

  // Get waste records for a session
  getWasteRecords: async (sessionId: number | string) => {
    const response = await api.get(`/api/operator/waste/session/${sessionId}`);
    return response.data;
  },

  // Delete a waste record
  deleteWasteRecord: async (wasteId: number) => {
    const response = await api.delete(`/api/operator/waste/${wasteId}`);
    return response.data;
  },

  // Delete all waste records for a sub-session (for re-saving updated values)
  deleteSessionWasteRecords: async (subSessionNumber: string) => {
    const response = await api.delete(`/api/operator/waste/session/${subSessionNumber}`);
    return response.data;
  },

  // Get marketable byproduct buffer state (for showing carry-forward vs session contribution)
  getMarketableBufferState: async (
    sourceMaterialItemNumber: string,
    byproductItemNumber: string
  ): Promise<{
    exists: boolean;
    buffer_id: string | null;
    source_material_item_number: string;
    byproduct_item_number: string;
    reserved_weight: number;
    ready_case_count: number;
    ready_case_weight: number;
    printed_case_count?: number;
    printed_case_weight?: number;
    total_buffer_weight: number;
    last_session_number: string | null;
    last_sub_session_number?: string | null;
    last_production_date?: string | null;
  }> => {
    const response = await api.get('/api/operator/waste/marketable-buffer', {
      params: { sourceMaterialItemNumber, byproductItemNumber }
    });
    return response.data;
  },

  // Get marketable byproduct buffer state for multiple items (batch)
  getMarketableBufferStateBatch: async (
    items: Array<{ sourceMaterialItemNumber: string; byproductItemNumber: string }>
  ): Promise<{
    buffers: Record<string, {
      exists: boolean;
      buffer_id?: string;
      reserved_weight: number;
      ready_case_count: number;
      ready_case_weight: number;
      total_buffer_weight: number;
      last_session_number?: string | null;
    }>;
  }> => {
    const response = await api.get('/api/operator/waste/marketable-buffer/batch', {
      params: { items: JSON.stringify(items) }
    });
    return response.data;
  },

  // Get raw materials for a session (for remainder dropdown)
  getSessionMaterials: async (sessionId: string, isSubSession: boolean = false): Promise<{
    materials: Array<{
      item_number: string;
      item_name: string;
      item_chinese_name: string;
      transfer_number: string;
      allocated_weight: number;
      remainder_weight: number;
    }>
  }> => {
    const response = await api.get(`/api/operator/sessions/${sessionId}/materials`, {
      params: isSubSession ? { is_sub_session: 'true' } : undefined,
    });
    return response.data;
  },

  // Get finish_product_ni carryover allocated TO a sub-session (WCO-2116)
  getFinishProductCarryover: async (subSessionNumber: string): Promise<{
    carryover: Array<{
      lot_id: string;
      item_number: string;
      item_name: string;
      allocated_weight: number | string;
      breakdown: Array<{
        source_sub_session_number: string;
        source_session_number: string | null;
        weight: number;
      }>;
    }>;
  }> => {
    const response = await api.get(`/api/operator/sessions/${subSessionNumber}/finish-product-carryover`);
    return response.data;
  },

  // Get sub-session details by ID (for Supervisor Override modal)
  getSubSessionDetails: async (subSessionNumber: string): Promise<{ session: ProductionSession | null }> => {
    const response = await api.get(`/api/operator/sessions/sub-session/${subSessionNumber}`);
    const data = response.data;
    if (data.session) data.session = normalizeSessionYield(data.session);
    return data;
  },

  // Get pending box for a sub-session (for resuming flow after page reload)
  getPendingBox: async (subSessionNumber: string): Promise<{
    success: boolean;
    pending_box: {
      id: string;
      box_number: string;
      barcode_number: string;
      actual_weight: number;
      lot_number: string;
      status: string;
      created_at: string;
    } | null;
  }> => {
    const response = await api.get(`/api/operator/sessions/${subSessionNumber}/pending-box`);
    return response.data;
  },

  // Label Management - Record label print
  recordLabelPrint: async (data: {
    production_inventory_id: string;
    session_number: string;
    box_number: string;
    lot_number: string;
    printed_by: string;
    printer_name?: string;
    label_data: any;
    barcode_number?: string;
    is_reprint?: boolean;
    reprint_reason?: string;
  }) => {
    const response = await api.post('/api/operator/labels/print', data);
    return response.data;
  },

  // Get all boxes with label status for a session
  getSessionBoxes: async (sessionId: string, status: string = 'all') => {
    const response = await api.get(`/api/operator/labels/session/${sessionId}/boxes`, {
      params: { status }
    });
    return response.data;
  },

  // Reprint label (supervisor action)
  reprintLabel: async (inventoryId: string, data: {
    supervisor_id: string;
    reason: string;
    printer_name?: string;
    label_data: any;
  }) => {
    const response = await api.post(`/api/operator/labels/box/${inventoryId}/reprint`, data);
    return response.data;
  },

  // Print label via print-service. If print_service_url is set in config_settings,
  // requests go directly to the factory LAN print service (bypassing the cloud API gateway).
  printLabel: async (data: {
    label_data: Record<string, any>;
    barcode_data: string;
    barcode_display_text: string;
    copies?: number;
    is_reprint?: boolean;
    station_id?: number;
    printer_mac?: string; // Direct MAC address — skips localhost API calls on print service
    printer_ip?: string; // Static IP — bypasses MAC/ARP resolution entirely
  }): Promise<{ success: boolean; message?: string; error?: string; error_code?: string }> => {
    const base = getPrintServiceUrl();
    const url = base ? `${base}/api/print/html-label` : `${API_BASE_URL}/api/print/html-label`;
    const response = await axios.post(url, data, { headers: authHeaders() });
    return response.data;
  },

  // ============================================
  // Clock-In / Clock-Out Functions
  // ============================================

  // Scan station barcode to get station info and active session
  scanStation: async (barcode: string): Promise<{
    success: boolean;
    station?: {
      station_id: number;
      station_name: string;
      station_barcode: string;
      status: string;
      station_type: string;
      location_code?: string;
    };
    session?: {
      sub_session_number: string;
      status: string;
      started_at: string;
      paused_duration_seconds?: number;
      initial_material_weight: number;
      target_output_weight: number;
      actual_output_weight: number;
      product_sku: string;
      product_name: string;
      product_chinese: string;
      lot_number: string;
      packing?: string;
      product_unit_weight?: number;
      expected_boxes?: number;
      total_boxes?: number;
      // Raw material details
      raw_material_item_number?: string;
      raw_material_english?: string;
      raw_material_chinese?: string;
      raw_material_brand?: string;
      raw_material_yield?: number;
      raw_material_yield_source?: string;
      material_location_code?: string;
    } | null;
    assignments?: {
      operator: WorkerAssignment | null;
      packer: WorkerAssignment | null;
    };
    error?: string;
    message?: string;
    errorCode?: string;
    sub_session_number?: string;
  }> => {
    const response = await api.post('/api/operator/clockin/scan-station', { barcode });
    const data = response.data;
    if (data.session) {
      if (data.session.raw_material_yield != null) data.session.raw_material_yield = Number(data.session.raw_material_yield) / 100;
    }
    return data;
  },

  // Look up employee by badge code
  lookupEmployee: async (badgeCode: string): Promise<{
    success: boolean;
    employee?: {
      employee_number: string;
      display_name: string;
      user_id: string | null;
      user_level?: string;  // O=Operator, S=Supervisor, M=Manager, A=Admin
      is_supervisor?: boolean;
    };
    existing_assignment?: {
      sub_session_number: string;
      station_id: number;
      role: string;
      clocked_in_at: string;
      station_name: string;
    } | null;
    error?: string;
    message?: string;
  }> => {
    const response = await api.post('/api/operator/clockin/lookup-employee', { badge_code: badgeCode });
    return response.data;
  },

  // Clock in to a station with a role
  clockIn: async (data: {
    station_id: number;
    badge_code: string;
    role: 'operator' | 'packer';
    confirm_relief?: boolean;
    auto_transfer?: boolean;
  }): Promise<{
    success: boolean;
    action?: 'assigned' | 'needs_confirmation' | 'needs_transfer' | 'already_assigned';
    user?: {
      user_id: string;
      employee_number: string;
      name: string;
    };
    role?: string;
    session?: {
      sub_session_number: string;
      status: string;
    };
    assignments?: {
      operator: WorkerAssignment | null;
      packer: WorkerAssignment | null;
    };
    redirect_to?: string;
    current_user?: WorkerAssignment;
    new_user?: {
      user_id: string;
      employee_number: string;
      name: string;
    };
    existing_station?: string;
    existing_role?: string;
    current_role?: string;
    employee_box_count?: number;  // Boxes packed by this employee in this shift
    error?: string;
    message?: string;
  }> => {
    const response = await api.post('/api/operator/clockin/clock-in', data);
    return response.data;
  },

  // Clock out from current station
  clockOut: async (badgeCode: string, stationId?: number, reason?: string): Promise<{
    success: boolean;
    message?: string;
    clocked_out_from?: {
      station_name: string;
      role: string;
      clocked_in_at: string;
    };
    error?: string;
  }> => {
    const response = await api.post('/api/operator/clockin/clock-out', {
      badge_code: badgeCode,
      station_id: stationId,
      ...(reason && { reason }),
    });
    return response.data;
  },

  // Get current assignments for a station
  getStationAssignments: async (stationId: string): Promise<{
    success: boolean;
    sub_session_number?: string;
    assignments?: {
      operator: WorkerAssignment | null;
      packer: WorkerAssignment | null;
    };
  }> => {
    const response = await api.get(`/api/operator/clockin/assignments/${stationId}`);
    return response.data;
  },

  // Get employee's current clock-in status
  getMyClockInStatus: async (employeeNumber: string): Promise<{
    success: boolean;
    clocked_in: boolean;
    assignment?: {
      sub_session_number: string;
      station_id: number;
      role: string;
      clocked_in_at: string;
      station_name: string;
      station_barcode: string;
      session_status: string;
      product_name: string;
    } | null;
  }> => {
    const response = await api.get(`/api/operator/clockin/my-status/${employeeNumber}`);
    return response.data;
  },

  // Confirm box with double-scan (badge + box label)
  confirmBoxScan: async (data: {
    badge_code: string;
    box_label: string;
    sub_session_number: string;
  }): Promise<{
    success: boolean;
    message?: string;
    box?: {
      id: string;
      box_number: number;
      barcode_number: string;
      actual_weight: number;
    };
    packed_by?: {
      user_id: string;
      name: string;
      employee_number: string;
    };
    progress?: {
      current_boxes: number;
      expected_boxes: number;
      target_reached: boolean;
    };
    error?: string;
  }> => {
    const response = await api.post('/api/operator/clockin/confirm-box', data);
    return response.data;
  },

  // Get activity log for a station
  getActivityLog: async (stationId: number, params?: {
    date?: string;
    sub_session_number?: string;
    limit?: number;
  }): Promise<{
    success: boolean;
    activity_log: Array<{
      id: string;
      event_type: string;
      station_id: number;
      sub_session_number: string;
      user_id: string;
      employee_number: string;
      role: string;
      related_user_id: string | null;
      box_label: string | null;
      details: any;
      created_at: string;
      user_name: string;
      station_name: string;
    }>;
  }> => {
    const response = await api.get(`/api/operator/clockin/activity-log/${stationId}`, { params });
    return response.data;
  },

  // Record a supervisor override action (label reprint, manual weight, yield sign-off, EOD sign-off, session completion)
  recordSupervisorOverride: async (data: {
    override_type: 'label_reprint' | 'manual_weight' | 'yield_signoff' | 'eod_signoff' | 'session_completion';
    supervisor_id: string;
    supervisor_name: string;
    session_number?: string;
    sub_session_number?: string;
    operator_employee_number?: string;
    operator_name?: string;
    reason?: string;
    details?: Record<string, unknown>;
  }): Promise<{ success: boolean; record: unknown }> => {
    const response = await api.post('/api/supervisor/override', data);
    return response.data;
  },

  // Void a pending label
  voidPendingLabel: async (boxId: string, supervisorEmployeeNumber: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/api/operator/labels/${boxId}/void`, { supervisor_employee_number: supervisorEmployeeNumber });
    return response.data;
  },

  // Report damaged raw material
  reportMaterialIssue: async (data: {
    sub_session_number: string;
    damaged_weight: number;
    reason: string;
    barcode_raw?: string;
    weight_source: 'barcode' | 'manual';
    operator_employee_number: string;
    operator_name: string;
  }) => {
    const response = await api.post('/api/operator/material-issue', data);
    return response.data;
  },

  // Get material issue history for a sub-session
  getMaterialIssues: async (subSessionNumber: string) => {
    const response = await api.get(`/api/operator/material-issue/sub-session/${subSessionNumber}`);
    return response.data;
  },

  // Dev Tools: is it enabled on this deployment? (WCO-4142 — only where DEV_TOOLS_ENABLED=true)
  getDevToolsStatus: async (): Promise<{ enabled: boolean }> => {
    const response = await api.get('/api/supervisor/dev-tools/status');
    return response.data;
  },

  // Dev Tools: Reset HTE-generated data. target controls which databases to clean.
  // target: 'all' | 'edge' | 'staging-vm' — production targets are not supported
  resetHteData: async (target: string = 'all'): Promise<{
    status: string;
    target: string;
    edge: Record<string, number>;
    stagingVm: Record<string, number>;
    errors: string[];
  }> => {
    const response = await api.post(`/api/supervisor/dev-tools/reset-hte?target=${target}`);
    return response.data;
  },

  listDataRepairActions: async (): Promise<DataRepairActionMeta[]> => {
    const response = await api.get('/api/supervisor/dev-tools/data-repair/actions');
    return response.data?.data?.actions ?? response.data?.actions ?? [];
  },

  listDataRepairHistory: async (limit = 25): Promise<DataRepairHistoryEntry[]> => {
    const response = await api.get(`/api/supervisor/dev-tools/data-repair/history?limit=${limit}`);
    return response.data?.data?.entries ?? response.data?.entries ?? [];
  },

  previewDataRepair: async (actionId: string, input: Record<string, unknown> = {}): Promise<DataRepairPreviewResult> => {
    const response = await api.post(`/api/supervisor/dev-tools/data-repair/${actionId}/preview`, input);
    return response.data?.data ?? response.data;
  },

  applyDataRepair: async (
    actionId: string,
    input: Record<string, unknown> = {},
    previewFingerprint: string,
  ): Promise<DataRepairApplyResult> => {
    const response = await api.post(`/api/supervisor/dev-tools/data-repair/${actionId}/apply`, {
      ...input,
      previewFingerprint,
    });
    return response.data?.data ?? response.data;
  },

  rollbackDataRepair: async (actionId: string, repairLogId: number): Promise<DataRepairRollbackResult> => {
    const response = await api.post(`/api/supervisor/dev-tools/data-repair/${actionId}/rollback`, { repairLogId });
    return response.data?.data ?? response.data;
  },

  // ============================================
  // Centralized Employee Display Info
  // ============================================

  // Look up a single employee's display info by employee number
  getEmployee: async (employeeNumber: string): Promise<EmployeeDisplayInfo | null> => {
    try {
      const response = await api.get(`/api/operator/employee/${employeeNumber}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  // Batch look up multiple employees' display info
  getEmployeeBatch: async (employeeNumbers: string[]): Promise<EmployeeDisplayInfo[]> => {
    if (employeeNumbers.length === 0) return [];
    const response = await api.get('/api/operator/employees/batch', {
      params: { numbers: employeeNumbers.join(',') }
    });
    return response.data;
  },
};

// Type for centralized employee display info
export interface EmployeeDisplayInfo {
  employee_number: string;
  display_name: string;
  full_name: string | null;
  first_name: string | null;
  middle_name: string | null;
  user_id: string | null;
  user_level: string | null;
  is_supervisor: boolean;
  is_active: boolean;
}

// Type for worker assignment
export interface WorkerAssignment {
  user_id: string;
  employee_number: string;
  name: string;
  clocked_in_at: string;
  on_break?: boolean;
}

export interface DataRepairParamSpec {
  name: string;
  description: string;
  example?: unknown;
  required?: boolean;
}

/** One row of the repair history list — enough to identify and roll back a past repair. */
export interface DataRepairHistoryEntry {
  id: number;
  action_id: string;
  operation: 'apply' | 'rollback';
  actor_id: string | null;
  actor_name: string | null;
  performed_at: string;
  input_params: unknown;
  parent_repair_id: number | null;
  rolled_back: boolean;
  error_message: string | null;
  row_count: number;
}

export interface DataRepairActionMeta {
  id: string;
  description: string;
  reverseSync: boolean;
  /** What the JSON input accepts — shown to the operator (WCO-3277 review). */
  params?: DataRepairParamSpec[];
}

export interface DataRepairGuardResult {
  name: string;
  passed: boolean;
  expected?: unknown;
  actual?: unknown;
  message?: string;
}

export interface DataRepairPreviewResult {
  actionId: string;
  description: string;
  before: Record<string, unknown>[];
  after: Record<string, unknown>[];
  guards: DataRepairGuardResult[];
  allGuardsPassed: boolean;
  rowCount: number;
  dryRun: true;
  previewFingerprint: string;
}

export interface DataRepairApplyResult {
  repairLogId: number;
  actionId: string;
  before: Record<string, unknown>[];
  after: Record<string, unknown>[];
  guards: DataRepairGuardResult[];
  reverseSync?: {
    enabled: boolean;
    tables: string[];
    rowsStamped: number;
    stampedTargets?: Array<{ schemaQualifiedTable: string; idColumn: string; idValue: unknown }>;
  };
}

export interface DataRepairRollbackResult {
  repairLogId: number;
  parentRepairLogId: number;
  actionId: string;
  before: Record<string, unknown>[];
  after: Record<string, unknown>[];
}

export default operatorService;
